import Alert from "@mui/material/Alert"
import Button from "@mui/material/Button"
import Chip from "@mui/material/Chip"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogContentText from "@mui/material/DialogContentText"
import DialogTitle from "@mui/material/DialogTitle"
import Typography from "@mui/material/Typography"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"

import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { adminApi } from "../axios"
import AdminEntityAutocomplete from "./AdminEntityAutocomplete"

/** Add or remove MLS boards on one customer, from the Users list. */
export default function AdminUserMlsBoardsDialog({ open, user, onClose }) {
  const [boards, setBoards] = useState([])
  const [hiddenCount, setHiddenCount] = useState(0)
  const [selected, setSelected] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const [changed, setChanged] = useState(false)

  useEffect(() => {
    if (!open || !user) return
    const shown = user.mls_boards || []
    setBoards(shown)
    setHiddenCount(Math.max((user.mls_board_count || 0) - shown.length, 0))
    setSelected(null)
    setError(null)
    setChanged(false)
  }, [open, user])

  async function run(action) {
    setBusy(true)
    setError(null)
    try {
      await action()
      setChanged(true)
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not update MLS boards.")
    } finally {
      setBusy(false)
    }
  }

  function handleAdd() {
    if (!selected) return
    const board = { id: selected.id, name: selected.label }
    run(async () => {
      await adminApi.assignMlsToUser(board.id, user.id)
      setBoards((current) =>
        current.some((item) => String(item.id) === String(board.id))
          ? current
          : [...current, board]
      )
      setSelected(null)
    })
  }

  function handleRemove(board) {
    run(async () => {
      await adminApi.removeMlsFromUser(board.id, user.id)
      setBoards((current) => current.filter((item) => item.id !== board.id))
    })
  }

  return (
    <Dialog
      open={open}
      onClose={() => onClose(changed)}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>MLS boards</DialogTitle>
      <DialogContent>
        <Column gap={2} sx={{ mt: 1 }}>
          <DialogContentText>
            Boards on the CRM settings of{" "}
            <strong>{user?.name || user?.email}</strong>. Team members and
            concierges use their parent account&apos;s boards.
          </DialogContentText>
          <Row gap={1} sx={{ flexWrap: "wrap" }}>
            {boards.length ? (
              boards.map((board) => (
                <Chip
                  key={board.id}
                  label={board.name || board.id}
                  onDelete={busy ? undefined : () => handleRemove(board)}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No boards.
              </Typography>
            )}
          </Row>
          {hiddenCount ? (
            <Typography variant="caption" color="text.secondary">
              +{hiddenCount} more board{hiddenCount === 1 ? "" : "s"} not shown
              here; manage them from MLS management.
            </Typography>
          ) : null}
          <Row gap={1} sx={{ alignItems: "center" }}>
            <AdminEntityAutocomplete
              entity="mls"
              label="Add board"
              placeholder="Search MLS boards"
              value={selected}
              onChange={setSelected}
              sx={{ flex: 1 }}
              disabled={busy}
            />
            <Button
              variant="contained"
              onClick={handleAdd}
              disabled={!selected || busy}
            >
              Add
            </Button>
          </Row>
          {error ? <Alert severity="error">{String(error)}</Alert> : null}
        </Column>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(changed)}>Done</Button>
      </DialogActions>
    </Dialog>
  )
}

AdminUserMlsBoardsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  user: PropTypes.object,
  onClose: PropTypes.func.isRequired,
}
